import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Group,
  Select,
  Stack,
  Textarea,
  Alert,
  Accordion,
  Loader,
  ChipGroup,
  Text,
} from "@mantine/core";
import { generateVideo, getGenerationOptions } from "../api";
import { IconCoins, IconInfoCircle } from "@tabler/icons-react";
import mixpanel from "mixpanel-browser";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { useForm } from "@mantine/form";

export default function GeneratorOptionsVideo() {
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);
  const [selectedLength, setSelectedLength] = useState("5");
  const selectedImages = useStore((state) => state.selectedImages);
  const images = useStore((state) => state.galleryImages);
  const [optionSource, setOptionSource] = useState(null);
  const [options, setOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const plugins = useStore((state) => state.pluginData);

  const pluginOptions =
    plugins?.map((p) => ({ label: p.pluginTitle, value: p.id })) || [];

  useEffect(() => {
    getGenerationOptions(optionSource, "video").then((data) => {
      setOptions(data);
      let conf = {};
      for (let element of data.options) {
        conf[element.title] = [];
      }
    });
  }, [optionSource]);

  const nearEqual = (a, b) => {
    return Math.abs(a - b) < 0.075;
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      aspectRatio: "9:16",
      prompt: "",
      config: {},
    },
  });

  const handleChipClick = (option) => (event) => {
    const keyName = option.title;
    form.setFieldValue("config." + keyName, null);
  };

  let selectedImageDetails = null;
  if (selectedImages.length > 0) {
    // console.log("images.data", images?.data);
    selectedImageDetails = images?.data?.find(
      (img) => img.logicalImageId === selectedImages[0]
    );
  }

  // console.debug("selectedImageDetails", selectedImageDetails);
  let selectedImageAspectRatio = null;
  if (selectedImageDetails) {
    selectedImageAspectRatio =
      selectedImageDetails?.pixelImages[0]?.width /
      selectedImageDetails?.pixelImages[0]?.height;
  }

  // console.debug("selectedImageAspectRatio", selectedImageAspectRatio);

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        setIsSubmitting(true);
        var dtoConfig = {};
        for (var x in values.config) {
          const val = values.config[x];
          // check if val is an array:
          if (Array.isArray(val)) dtoConfig[x] = val;
          else dtoConfig[x] = [val];
        }
        const dto = {
          sourceId: optionSource,
          prompt: values.prompt,
          aspectRatio: values.aspectRatio,
          duration: selectedLength,
          inputImageId: selectedImages.length ? selectedImages[0] : null,
          config: dtoConfig,
        };
        generateVideo(dto)
          .then((data) => {
            let newJob = {
              uri: "c1b.jpg",
              clientId: Math.random().toString(36).substr(2, 9),
              ...data,
            };
            setJobs([newJob, ...jobs]);
            try {
              mixpanel.track("Video gen started");
            } catch {}
          })
          .catch((error) => {
            notifications.show({
              title: "An error occurred!",
              message: error.message,
              color: "red",
            });
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      })}
    >
      <Stack p="xs" gap={4}>
        <Textarea
          required
          label="Prompt"
          {...form.getInputProps("prompt")}
          minRows={3}
          maxRows={6}
          autosize
        />

        <Text fz="sm" mt="xs">
          Aspect ratio (coming soon)
        </Text>
        <ChipGroup size="xs" {...form.getInputProps(`aspectRatio`)}>
          <Group gap="xs">
            <Chip variant="light" size="xs" value="9:16">
              9:16 (Portrait)
            </Chip>
            <Chip disabled variant="light" size="xs" value="16:9">
              16:9 (Landscape)
            </Chip>
          </Group>
        </ChipGroup>

        <Select
          mt="xs"
          disabled
          label="Length"
          onChange={setSelectedLength}
          value={selectedLength}
          data={[
            { label: "5s", value: "5" },
            { label: "10s", value: "10" },
          ]}
        />

        {pluginOptions?.length > 0 && (
          <Select
            description="Load plugin options & styles"
            label="Style Plugins"
            onChange={setOptionSource}
            value={optionSource}
            clearable
            data={[...pluginOptions]}
          />
        )}

        {selectedImages.length === 0 && (
          <Alert
            mt="xl"
            color="yellow"
            title="No image selected"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            The video will be generated from the prompt only. We suggest to
            create an image first and then generate a video from it.
          </Alert>
        )}

        {selectedImages.length > 1 && (
          <Alert
            mt="xl"
            color="red"
            title="Multiple images selected"
            variant="light"
            p="xs"
            icon={<IconInfoCircle />}
          >
            You can only create one video from a single image at a time
          </Alert>
        )}

        {selectedImages.length === 1 &&
          nearEqual(selectedImageAspectRatio, 9.0 / 16.0) == false && (
            <Alert
              mt="xl"
              color="red"
              title="Aspect ratio mismatch"
              variant="light"
              p="xs"
              icon={<IconInfoCircle />}
            >
              The selected image has an aspect ratio of{" "}
              {selectedImageAspectRatio?.toFixed(2)}. The video will be
              generated with a 9:16 (portrait) aspect ratio. This may result in
              cropping or stretching of the image. We recommend using a 9:16
              image for best results.
            </Alert>
          )}

        <Accordion>
          {!options && <Loader />}

          {options &&
            options.options &&
            options.options
              .sort(function (b, a) {
                var x = a.title.toLowerCase();
                var y = b.title.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
              })
              .map((option) => (
                <Accordion.Item key={option.title} value={option.title}>
                  <Accordion.Control>{option.title}</Accordion.Control>
                  <Accordion.Panel>
                    {/* {option.values.length > 10 && (
                      <TextInput
                        size="xs"
                        mb="xs"
                        value={filters[option.title]}
                        onChange={(evt) =>
                          setFilters({
                            ...filters,
                            [option.title]: evt.target.value,
                          })
                        }
                      />
                    )} */}
                    <Chip.Group
                      multiple={option.allowMulti}
                      {...form.getInputProps(`config.${option.title}`)}
                    >
                      <Group gap="xs">
                        {option.values
                          // .filter((value) => {
                          //   if (!filters[option.title]) return true;
                          //   return value.titleUser
                          //     .toLowerCase()
                          //     .includes(filters[option.title].toLowerCase());
                          // })
                          .sort(function (a, b) {
                            var x = a.titleUser.toLowerCase();
                            var y = b.titleUser.toLowerCase();
                            return x < y ? -1 : x > y ? 1 : 0;
                          })
                          .map((value) => (
                            // <Tooltip
                            //   label={value.tooltip}
                            //   key={`${option.title}-${value.titleUser}`}
                            // >
                            <Chip
                              key={`${option.title}-${value.titleUser}`}
                              variant="light"
                              onClick={handleChipClick(option)}
                              size="xs"
                              value={value.titleUser}
                            >
                              {value.titleUser}
                            </Chip>
                            // </Tooltip>
                          ))}
                      </Group>
                    </Chip.Group>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
        </Accordion>

        <Button
          variant="light"
          rightSection={
            <Group gap={4}>
              <IconCoins stroke={1.5} size="15" /> 700
              {/* {50 * selectedLength} */}
            </Group>
          }
          disabled={selectedImages.length > 1}
          loading={isSubmitting}
          mt="sm"
          type="submit"
        >
          Create
        </Button>
      </Stack>
    </form>
  );
}
